import { useState } from "react";
import { useEffectAfterMount } from "@hooks/use_effect_after_mount.ts";
import { Location } from "@components/ui/location_input.tsx";
import { useShowFlash } from "@sprout/flash.tsx";
import { useTranslation } from "react-i18next";
import { type Option } from "@sprout/select";
import { useLoadAsyncOptions } from "@sprout/select/hooks";
import axios, { isAxiosError } from "axios";

export const useWorkPreferencesForm = (
  workPreferencesPath: string,
  currentEmploymentTypes?: Option[],
  currentLanguages?: Option[],
  currentLocation?: Location | null,
  currentRelocation?: boolean | null,
  currentWorksites?: Option[],
) => {
  const showFlash = useShowFlash();

  const [languages, setLanguages] = useState<Option[]>(currentLanguages ?? []);
  const loadLanguageOptions = useLoadAsyncOptions("/languages");
  const [languageError, setLanguageError] = useState<string | null>(null);
  const [location, setLocation] = useState<Location | null>(currentLocation ?? null);
  const [employmentTypes, setEmploymentTypes] = useState<Option[]>(currentEmploymentTypes ?? []);
  const [worksites, setWorksites] = useState<Option[]>(currentWorksites ?? []);
  const [relocation, setRelocation] = useState<boolean | null>(currentRelocation ?? null);
  const [relocationError, setRelocationError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const { t } = useTranslation("user");

  useEffectAfterMount(() => {
    setUnsavedChanges(true);
  }, [languages, employmentTypes, worksites, relocation, location, relocationError, error]);

  const submit = async (): Promise<boolean> => {
    setLoading(true);
    setError(null);
    setLanguageError(null);
    setRelocationError(null);

    try {
      const formData = new FormData();

      languages.forEach((language) => {
        formData.append("languages[][id]", language.value.toString());
      });

      employmentTypes.forEach((employmentType) => {
        formData.append("employment_types[][category]", employmentType.value.toString());
      });

      worksites.forEach((worksite) => {
        formData.append("worksites[][category]", worksite.value.toString());
      });

      formData.append("preference[relocation]", relocation?.toString() || "");
      formData.append("preference[location]", JSON.stringify(location));

      await axios.patch(workPreferencesPath, formData);

      showFlash(t("workPreferences.flashSuccess"), "success");
      setUnsavedChanges(false);
      return true;
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 422) {
        const { errors } = e.response.data;

        if (errors.user_language) {
          setLanguageError(errors.user_language[0]);
        }

        if (errors.relocation) {
          setRelocationError(errors.relocation[0]);
        }
      }

      console.error(e);
      setError(t("workPreferences.flashError"));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLanguages = (selectedLanguages: Option[]) => {
    if (selectedLanguages.length > 5) {
      return;
    }

    setLanguages(selectedLanguages);
  };

  return {
    languages,
    setLanguages,
    handleUpdateLanguages,
    loadLanguageOptions,
    languageError,
    employmentTypes,
    setEmploymentTypes,
    worksites,
    setWorksites,
    location,
    setLocation,
    relocation,
    setRelocation,
    relocationError,
    error,
    loading,
    unsavedChanges,
    submit,
  };
};

export type WorkPreferencesFormType = ReturnType<typeof useWorkPreferencesForm>;
